import React, { useState, useEffect, useCallback } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  TextField,
  Button,
  Grid,
  Typography,
  Card,
  MenuItem,
  Box,
  IconButton,
  Autocomplete,
} from "@mui/material";
import axios from "../../api";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useSelector } from "react-redux";
import AWS from "aws-sdk";
import { useDropzone } from "react-dropzone";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import DeleteIcon from "@mui/icons-material/Delete";
import { RootState } from "store";
import { AxiosError } from "axios";

const validationSchema = Yup.object({
  category_id: Yup.string().required("Category is required"),
  asset_type_id: Yup.string().required("Asset Type is required"),
  asset_id: Yup.string().required("Asset is required"),
  description: Yup.string().required("Description is required"),
  address_id: Yup.string().required("Address is required"),
  title: Yup.string().required("Title is required"),
});

const s3 = new AWS.S3({
  endpoint: process.env.REACT_APP_AWS_ENDPOINT,
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  region: process.env.REACT_APP_AWS_REGION,
  signatureVersion: "v4",
});

interface UploadedFile {
  name: string;
  type: string;
  size: number;
  preview: string;
  id: string; // Added for unique key
}

interface InitialValues {
  category_id: number;
  description: string;
  address_id: number;
  multimedia: Media[];
  asset_type_id: number;
  asset_id: number;
  title: string;
  assigned_employee_id: number;
}

interface Media {
  multimedia_id: string;
  file_name: string;
  file_type: string;
  file_path: string;
}

interface AssetType {
  asset_type_id: number;
  asset_type_name: string;
}

interface Category {
  category_id: number;
  name: string;
}

interface Asset {
  asset_id: number;
  asset_name: string;
}

interface Address {
  address_id: number;
  address_type: string;
}

interface MediaData {
  file_name: string;
  file_type: string;
  file_path: string;
}

export interface EditTicketProps {
  id?: string;
  embedded?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const EditTicket = ({ id: idProp, embedded, onSuccess, onCancel }: EditTicketProps = {}) => {
  const { id: idFromParams } = useParams();
  const id = idProp ?? idFromParams;
  const [categories, setCategories] = useState([]);
  const [assetData, setAssetData] = useState([]);
  const [assets, setAssets] = useState([]);
  const [selectedAssetType, setSelectedAssetType] = useState<AssetType | null>(
    null
  );
  const [initialCategory, setInitialCategory] = useState<Category | null>(null);
  const [addresses, setAddresses] = useState([]);
  const [ticketData, setTicketData] = useState<InitialValues | null>(null);
  const [initialValues, setInitialValues] = useState<InitialValues | null>(
    null
  );
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const { fetchError, fetchSuccess } = useSnackbar();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  // Helper function to upload a single file to S3
  const uploadFileToS3 = async (file: UploadedFile): Promise<MediaData> => {
    const fileName = `${Date.now()}-${file.name}`;
    const bucketName = process.env.REACT_APP_AWS_BUCKET_NAME;

    if (!bucketName) {
      throw new Error("AWS bucket configuration is missing");
    }

    const params: AWS.S3.PutObjectRequest = {
      Bucket: bucketName,
      Key: fileName,
      Body: file as any,
      ContentType: file.type,
      ACL: "public-read",
    };

    let type = file.type.split("/")[0];
    if (type === "image") {
      type = "Photo";
    }

    const mediaData: MediaData = {
      file_name: fileName,
      file_type: type,
      file_path: `https://${process.env.REACT_APP_AWS_BUCKET_NAME}.${process.env.REACT_APP_AWS_REGION}.digitaloceanspaces.com/${fileName}`,
    };

    return new Promise((resolve, reject) => {
      s3.upload(params, (err) => {
        if (err) {
          console.error("Error uploading file:", err);
          reject(new Error(`Failed to upload file: ${err.message}`));
        } else {
          resolve(mediaData);
        }
      });
    });
  };

  // Helper function to upload all files
  const uploadAllFiles = async (
    files: UploadedFile[]
  ): Promise<MediaData[]> => {
    return Promise.all(files.map(uploadFileToS3));
  };

  // Helper function to create ticket data
  const createTicketData = (values: InitialValues) => {
    return {
      category_id: values.category_id,
      asset_id: values.asset_id,
      asset_type_id: values.asset_type_id,
      description: values.description,
      address_id: values.address_id,
      customer_id: user?.userId,
      title: values.title,
    };
  };

  // Helper function to update ticket without files
  const updateTicketWithoutFiles = async (ticketData: any) => {
    const response = await axios.put(`/tickets/${id}`, { ticketData });
    fetchSuccess(response.data.message);
    if (embedded && onSuccess) onSuccess();
    else navigate("/customer/tickets-list");
  };

  // Helper function to update ticket with files
  const updateTicketWithFiles = async (
    ticketData: any,
    mediaData: MediaData[]
  ) => {
    const response = await axios.put(`/tickets/${id}`, {
      ticketData,
      mediaData,
    });
    fetchSuccess(response.data.message);
    if (embedded && onSuccess) onSuccess();
    else navigate("/customer/tickets-list");
  };

  // Main submit handler - refactored to reduce complexity
  const handleSubmit = async (values: InitialValues) => {
    setSubmitting(true);

    try {
      const ticketData = createTicketData(values);

      if (uploadedFiles.length > 0) {
        const updatedMediaData = await uploadAllFiles(uploadedFiles);
        await updateTicketWithFiles(ticketData, updatedMediaData);
      } else {
        await updateTicketWithoutFiles(ticketData);
      }
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      } else if (error instanceof Error) {
        fetchError(error.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (fileId: string) => {
    setUploadedFiles((prevFiles) =>
      prevFiles.filter((file) => file.id !== fileId)
    );
  };

  const dropzone = useDropzone({
    accept: {
      "image/png": [".png", ".jpeg", ".jpg"],
      "video/mp4": [".mp4"],
    },
    onDrop: (acceptedFiles) => {
      // Append new files with unique IDs to the existing uploadedFiles array
      setUploadedFiles((prevFiles) => [
        ...prevFiles,
        ...acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
            id: `${Date.now()}-${Math.random()}`, // Unique ID for key
          })
        ),
      ]);
    },
  });

  const fetchData = useCallback(async () => {
    try {
      const [assetTypeResponse, addressesResponse, ticketResponse] =
        await Promise.all([
          axios.get("/asset-types", { params: { is_active: 1 } }),
          axios.get(`/address/customer/${user?.userId}`),
          axios.get(`/tickets/${id}`),
        ]);
      setAssetData(assetTypeResponse.data.list);
      setAddresses(addressesResponse.data.list);
      const { data } = ticketResponse;
      const ticketData = data?.list;
      setTicketData(ticketData);
      setInitialValues({
        category_id: ticketData.category_id,
        description: ticketData.description,
        address_id: ticketData.address_id,
        multimedia: ticketData.multimedia,
        asset_type_id: ticketData.asset_type_id,
        asset_id: ticketData.asset_id,
        title: ticketData.title,
        assigned_employee_id: ticketData.assigned_employee_id,
      });

      setSelectedAssetType({
        asset_type_id: ticketData?.asset_type_id,
        asset_type_name: ticketData?.asset_type_name,
      });
      setSelectedAsset({
        asset_id: ticketData.asset_id,
        asset_name: ticketData?.asset_name,
      });
      setInitialCategory({
        category_id: ticketData.category_id || "",
        name: ticketData?.category_name,
      });
    } catch (error) {
      if (embedded && onCancel) onCancel();
      else navigate("/customer/tickets-list");
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
      console.error("Error fetching data", error);
    }
  }, [user, id, fetchError, navigate, embedded, onCancel]);

  const fetchAssetData = useCallback(async () => {
    try {
      const response = await axios.get(
        `/assets/${selectedAssetType?.asset_type_id}/${user?.userId}`
      );
      setAssets(response?.data?.list);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  }, [selectedAssetType, user]);

  useEffect(() => {
    fetchAssetData();
  }, [fetchAssetData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleMediaDelete = async (mediaId: string) => {
    try {
      const response = await axios.delete(`/tickets/media/${mediaId}`);
      fetchData();
      fetchSuccess(response.data.message);
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    }
  };

  useEffect(() => {
    const fetchCategoryData = async () => {
      try {
        const categoriesResponse = await axios.get(
          `/category/asset-type/${selectedAssetType?.asset_type_id}`,
          {
            params: {
              is_active: 1,
            },
          }
        );
        setCategories(categoriesResponse.data.list);
      } catch (err) {
        console.error("Error fetching data", err);
      }
    };
    fetchCategoryData();
  }, [selectedAssetType]);

  if (!initialValues) {
    return <Typography>Loading...</Typography>;
  }

  const Wrapper: React.ElementType = embedded ? Box : Card;
  const wrapperProps = embedded
    ? { sx: { width: "100%", pt: 0, px: 0 } }
    : {
        style: {
          padding: "24px",
          width: "100%",
          borderRadius: "8px",
        },
      };

  const formContent =
    !ticketData?.assigned_employee_id ? (
      <Wrapper {...wrapperProps}>
        <SoftTypography variant="h5" sx={{ marginBottom: 2 }}>
          Edit Ticket
        </SoftTypography>
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, setFieldValue }) => (
              <Form
                className="edit-ticket-form"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <SoftBox sx={{ display: "block" }}>
                      <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                        Title
                      </SoftTypography>
                      <Field
                        name="title"
                        as={TextField}
                        variant="outlined"
                        fullWidth
                        error={!!errors.title && touched.title}
                        helperText={touched.title && errors.title}
                      />
                    </SoftBox>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SoftBox sx={{ display: "block" }}>
                      <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                        Asset Type
                      </SoftTypography>
                      <Autocomplete
                        options={assetData || []}
                        getOptionLabel={(option) => option.asset_type_name}
                        value={selectedAssetType}
                        onChange={(event, value) => {
                          setFieldValue(
                            "asset_type_id",
                            value?.asset_type_id || ""
                          );
                          setSelectedAssetType(value);
                          setSelectedAsset(null);
                          setInitialCategory(null);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={
                              !!errors.asset_type_id && touched.asset_type_id
                            }
                            helperText={
                              touched.asset_type_id && errors.asset_type_id
                            }
                          />
                        )}
                      />
                    </SoftBox>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SoftBox sx={{ display: "block" }}>
                      <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                        Category
                      </SoftTypography>
                      <Autocomplete
                        options={categories || []}
                        getOptionLabel={(option) => option.name}
                        value={initialCategory}
                        onChange={(event, value) => {
                          setFieldValue(
                            "category_id",
                            value?.category_id || ""
                          );
                          setInitialCategory(value);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={!!errors.category_id && touched.category_id}
                            helperText={
                              touched.category_id && errors.category_id
                            }
                          />
                        )}
                      />
                    </SoftBox>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SoftBox sx={{ display: "block" }}>
                      <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                        Asset
                      </SoftTypography>
                      <Autocomplete
                        options={assets || []}
                        getOptionLabel={(option) => option.asset_name}
                        value={selectedAsset}
                        onChange={(event, value) => {
                          setFieldValue("asset_id", value?.asset_id || "");
                          setSelectedAsset(value);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={!!errors.asset_id && touched.asset_id}
                            helperText={touched.asset_id && errors.asset_id}
                          />
                        )}
                      />
                    </SoftBox>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SoftBox sx={{ display: "block" }}>
                      <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                        Description
                      </SoftTypography>
                      <Field
                        name="description"
                        as={TextField}
                        variant="outlined"
                        fullWidth
                        error={!!errors.description && touched.description}
                        helperText={touched.description && errors.description}
                      />
                    </SoftBox>
                  </Grid>
                  <Grid item xs={12}>
                    <SoftBox sx={{ display: "block" }}>
                      <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                        Address
                      </SoftTypography>
                      <Field
                        name="address_id"
                        as={TextField}
                        select
                        variant="outlined"
                        fullWidth
                        error={!!errors.address_id && touched.address_id}
                        helperText={touched.address_id && errors.address_id}
                      >
                        {addresses.map((address: Address) => (
                          <MenuItem
                            key={address.address_id}
                            value={address.address_id}
                          >
                            {address.address_type}
                          </MenuItem>
                        ))}
                      </Field>
                    </SoftBox>
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <div
                      className={
                        (ticketData?.multimedia &&
                          ticketData.multimedia.length > 0) ||
                        uploadedFiles?.length > 0
                          ? "upload-media"
                          : ""
                      }
                    >
                      {ticketData?.multimedia && (
                        <div>
                          {ticketData.multimedia.map((media) => (
                            <div
                              className="media-container"
                              key={media.multimedia_id}
                              style={{ position: "relative" }}
                            >
                              {media.file_type === "Photo" ? (
                                <img
                                  className="upload-img"
                                  src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                                  alt={`Uploaded media: ${media.file_name}`}
                                />
                              ) : (
                                <video
                                  className="upload-video"
                                  controls
                                  width="100%"
                                  height="140px"
                                >
                                  <source
                                    src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                                    type="video/mp4"
                                  />
                                  <track
                                    kind="captions"
                                    src=""
                                    label="No captions available"
                                  />
                                </video>
                              )}
                              <IconButton
                                onClick={() =>
                                  handleMediaDelete(media?.multimedia_id)
                                }
                                style={{
                                  position: "absolute",
                                  top: 5,
                                  right: 5,
                                }}
                                color="secondary"
                                aria-label={`Delete media ${media.file_name}`}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </div>
                          ))}
                        </div>
                      )}
                      {uploadedFiles.map((file) => (
                        <div
                          className="media-container"
                          key={file.id}
                          style={{ position: "relative" }}
                        >
                          {file.preview &&
                            (file.type.startsWith("image") ? (
                              <img
                                src={file.preview}
                                alt={`Uploaded media: ${file.name}`}
                              />
                            ) : (
                              <video controls width="100%" height="140px">
                                <source src={file.preview} type="video/mp4" />
                                <track
                                  kind="captions"
                                  src=""
                                  label="No captions available"
                                />
                              </video>
                            ))}
                          <IconButton
                            onClick={() => handleDelete(file.id)}
                            style={{ position: "absolute", top: 5, right: 5 }}
                            color="secondary"
                            aria-label={`Delete ${file.name}`}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </div>
                      ))}
                    </div>
                    <Box
                      sx={{ cursor: "pointer", width: "fit-content" }}
                      {...dropzone.getRootProps()}
                    >
                      <Box>
                        <label htmlFor="image-upload-input">
                          <input
                            {...dropzone.getInputProps({
                              name: "file_name",
                            })}
                          />
                          <Button
                            className="upload-btn"
                            component="span"
                            variant="contained"
                            color="primary"
                          >
                            Add/Upload File
                          </Button>
                        </label>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      className="success-btn"
                      sx={{
                        position: "relative",
                        minWidth: 100,
                      }}
                      variant="contained"
                      disabled={submitting}
                      type="submit"
                      color="success"
                    >
                      Save
                    </Button>

                    <Button
                      className="cancel-btn"
                      sx={{
                        position: "relative",
                        minWidth: 100,
                        ml: 2.5,
                      }}
                      variant="contained"
                      color="warning"
                      onClick={() =>
                        embedded && onCancel
                          ? onCancel()
                          : navigate("/customer/tickets-list")
                      }
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Form>
            )}
          </Formik>
      </Wrapper>
    ) : null;

  if (embedded) return <>{formContent}</>;
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {formContent}
    </DashboardLayout>
  );
};

export default EditTicket;
